import { twApi } from '../api';
import {
  CreateFuelCardConfigParams,
  DeleteFuelCardConfigParams,
  FuelCardConfig,
  FuelCardConfigResponse,
  FuelCardProvider,
  FuelCardResponse,
  FuelCardSyncRawResponse,
  FuelCardSyncStatusResponse
} from './fuelcard.model';

export const convertToFuelCardProvider = (provider: string): FuelCardProvider => {
  switch (provider.toLowerCase()) {
    case 'efs':
      return FuelCardProvider.EFS;
    default:
      throw new Error(`Unsupported fuel card provider: ${provider}`);
  }
};

const fuelcardApi = twApi.injectEndpoints({
  endpoints: (builder) => ({
    getFuelCardConfigs: builder.query<FuelCardConfigResponse[], string>({
      query: (divisionId) => {
        if (!divisionId) throw new Error('Divsion id required');
        return {
          url: `/fuelcard/config`,
          method: 'GET',
          params: {
            divisionId
          }
        };
      },
      transformResponse: (response: FuelCardConfig[]) => {
        return response.flatMap((config) =>
          config.credentials.map((cred) => ({
            fuelcardProvider: convertToFuelCardProvider(config.fuelcardProvider),
            tenantId: config.tenantId,
            divisionId: config.divisionId,
            username: cred.username,
            password: cred.password
          }))
        );
      },
      providesTags: (result, error, id) => [
        { type: 'FuelCardConfig' as const, id },
        { type: 'FuelCardConfig' as const, id: 'FUEL_CARD_CONFIG_LIST' }
      ]
    }),

    addFuelCardConfig: builder.mutation<string, CreateFuelCardConfigParams>({
      query: (arg) => {
        const { divisionId, ...rest } = arg;
        return {
          url: '/fuelcard/config',
          body: { ...rest, fuelcardProvider: rest.fuelcardProvider.toLowerCase() },
          method: 'POST',
          params: { divisionId },
          responseHandler: async (response): Promise<string> => response.text()
        };
      },
      invalidatesTags: [{ type: 'FuelCardConfig', id: 'FUEL_CARD_CONFIG_LIST' }],
      extraOptions: { responseHandler: 'text' }
    }),

    deleteFuelCardConfig: builder.mutation<string, DeleteFuelCardConfigParams>({
      query: (arg) => {
        return {
          url: '/fuelcard/config',
          method: 'DELETE',
          params: { ...arg, fuelcardProvider: arg.fuelcardProvider.toLowerCase() },
          responseHandler: async (response): Promise<string> => response.text()
        };
      },
      invalidatesTags: [{ type: 'FuelCardConfig', id: 'FUEL_CARD_CONFIG_LIST' }],
      extraOptions: { responseHandler: 'text' }
    }),

    syncFuelTransactions: builder.mutation<string, string>({
      query: (divisionId) => {
        return {
          url: '/fuelcard/retry',
          method: 'POST',
          params: { divisionId },
          responseHandler: async (response): Promise<string> => response.text()
        };
      },
      invalidatesTags: [
        { type: 'FuelTransactions', id: 'FUEL_TRANSACTIONS_LIST' },
        { type: 'FuelCardSyncStatus', id: 'FUEL_CARD_SYNC_STATUS_LIST' }
      ],
      extraOptions: { responseHandler: 'text' }
    }),

    getFuelSyncStatus: builder.query<FuelCardSyncStatusResponse[], string>({
      query: (divisionId) => {
        return {
          url: '/fuelcard/status',
          method: 'GET',
          params: { divisionId }
        };
      },
      transformResponse: (response: FuelCardSyncRawResponse[]) => {
        return response.map((sync) => ({
          lastSuccess: sync.lastSuccess,
          updated_at: sync.updated_at,
          username: sync.user,
          divisionId: sync.division,
          status: sync.status,
          provider: convertToFuelCardProvider(sync.provider)
        }));
      },
      providesTags: (result, error, id) => [
        { type: 'FuelCardSyncStatus' as const, id },
        { type: 'FuelCardSyncStatus' as const, id: 'FUEL_CARD_SYNC_STATUS_LIST' }
      ]
    }),
    getFuelCards: builder.query<FuelCardResponse, void>({
      query: () => ({
        url: '/fuel-card/cards',
        method: 'GET'
      }),
      providesTags: () => [
        { type: 'FuelCards' as const, id: 'FUEL_CARD_LIST' }
      ]
    })
  }),
  overrideExisting: false
});

export const {
  useGetFuelCardConfigsQuery,
  useAddFuelCardConfigMutation,
  useDeleteFuelCardConfigMutation,
  useSyncFuelTransactionsMutation,
  useGetFuelSyncStatusQuery,
  useGetFuelCardsQuery
} = fuelcardApi;
